import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import MapComponent from '../components/MapComponent';
import toast from 'react-hot-toast';
import { apiUrl } from '../utils/apiBase';
import { mapLotUnitForBackend, normalizeLotUnitForForm } from '../utils/formatters';

const BEDROOM_OPTIONS = Array.from({ length: 11 }, (_, idx) => String(idx));

const MAX_MEDIA_COUNT = 50;
const TARGET_MAX_FILE_SIZE = 6 * 1024 * 1024; // 6MB after compression
const HARD_MAX_FILE_SIZE = 20 * 1024 * 1024; // Reject anything above 20MB outright
const MAX_IMAGE_DIMENSION = 1920;
const MIN_IMAGE_DIMENSION = 640;
const QUALITY_STEPS = [0.85, 0.75, 0.65, 0.55, 0.45, 0.35];

const ensureJpegName = (originalName) => {
  if (!originalName) return `image-${Date.now()}.jpg`;
  const base = originalName.replace(/\.[^/.]+$/u, '');
  return `${base || 'image'}.jpg`;
};

const loadImageElement = (blob) => new Promise((resolve, reject) => {
  const img = new Image();
  img.onload = () => {
    URL.revokeObjectURL(img.src);
    resolve(img);
  };
  img.onerror = (err) => {
    URL.revokeObjectURL(img.src);
    reject(err);
  };
  img.src = URL.createObjectURL(blob);
});

async function compressImageFile(file, { maxSizeBytes = TARGET_MAX_FILE_SIZE, maxDimension = MAX_IMAGE_DIMENSION } = {}) {
  if (!(file instanceof File)) {
    throw new Error('Invalid file provided');
  }

  if (!file.type.startsWith('image/')) {
    throw new Error('Unsupported file type');
  }

  if (file.size <= maxSizeBytes) {
    return file;
  }

  let workingBlob = file;
  let currentMaxDimension = maxDimension;

  while (true) {
    const imageElement = await loadImageElement(workingBlob);
    const { width, height } = imageElement;
    const scale = Math.min(1, currentMaxDimension / width, currentMaxDimension / height);
    const canvas = document.createElement('canvas');
    canvas.width = Math.max(1, Math.round(width * scale));
    canvas.height = Math.max(1, Math.round(height * scale));

    const ctx = canvas.getContext('2d');
    ctx.drawImage(imageElement, 0, 0, canvas.width, canvas.height);

    let lastBlob = null;
    for (const quality of QUALITY_STEPS) {
      // eslint-disable-next-line no-await-in-loop
      lastBlob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', quality));
      if (lastBlob && lastBlob.size <= maxSizeBytes) {
        return new File([lastBlob], ensureJpegName(file.name), {
          type: 'image/jpeg',
          lastModified: Date.now(),
        });
      }
    }

    if (!lastBlob) {
      throw new Error('Unable to compress image');
    }

    if (currentMaxDimension <= MIN_IMAGE_DIMENSION) {
      throw new Error('Image is too large even after compression');
    }

    // Prepare for another iteration with a smaller dimension cap
    workingBlob = lastBlob;
    currentMaxDimension = Math.max(MIN_IMAGE_DIMENSION, Math.round(currentMaxDimension * 0.85));
  }
}

function normalizeSaleType(value) {
  const text = value ? value.toString().toLowerCase() : '';
  if (text.includes('rent')) return 'for_rent';
  if (text.includes('owner')) return 'for_sale_by_owner';
  return 'for_sale';
}

function normalizePropertyCategory(value) {
  const text = value ? value.toString().toLowerCase() : '';
  if (text.includes('apartment') || text.includes('condo') || text.includes('flat')) return 'apartment';
  if (text.includes('house') || text.includes('home') || text.includes('villa')) return 'house';
  return 'land_lot';
}

function normalizeCurrency(value) {
  const text = value ? value.toString().toLowerCase() : '';
  if (text.includes('eur') || text.includes('€')) return 'EUR';
  if (text.includes('cny') || text.includes('¥') || text.includes('yuan') || text.includes('rmb')) return 'CNY';
  return 'USD';
}

function extractNumericValue(value) {
  if (value === null || value === undefined) return null;
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null;
  }

  const cleaned = value.toString().replace(/[^0-9.,-]/g, '').replace(/,/g, '');
  if (!cleaned) return null;
  const numeric = parseFloat(cleaned);
  return Number.isNaN(numeric) ? null : numeric;
}

function generateListingTitle(propertyCategory, saleType) {
  const categoryLabel = propertyCategory === 'apartment'
    ? 'Apartment'
    : propertyCategory === 'house'
      ? 'House'
      : 'Land / Lot';
  const saleLabel = saleType === 'for_rent'
    ? 'For rent'
    : saleType === 'for_sale_by_owner'
      ? 'For sale by owner'
      : 'For sale';
  return `${categoryLabel} - ${saleLabel}`;
}

const initialFormState = {
  propertyCategory: 'land_lot',
  saleType: 'for_sale',
  isOwner: 'yes',
  contactName: '',
  phone: '',
  whatsapp: '',
  email: '',
  hasTitle: 'yes',
  address: '',
  city: '',
  region: '',
  country: '',
  roadAccess: '',
  roadSurface: '',
  inCommunity: '',
  waterConnection: '',
  electricity: '',
  lotSizeValue: '',
  lotSizeUnit: 'sqm',
  livingAreaValue: '',
  livingAreaUnit: 'sqm',
  bedrooms: '0',
  bathrooms: '0',
  yearBuilt: '',
  price: '',
  currency: 'USD',
  description: '',
  latitude: '',
  longitude: '',
  autoApprove: true,
  allowAgentContact: null,
};

const AdminPropertyUpload = ({ onPropertyCreated }) => {
  const { t } = useTranslation();
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploadedMedia, setUploadedMedia] = useState([]); // { path, url, type }[] after upload-media
  const [uploadMediaProgress, setUploadMediaProgress] = useState(0);
  const [uploadMediaStatus, setUploadMediaStatus] = useState('idle'); // idle | uploading | done | error
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(null); // 0–100 when uploading images, null for JSON or idle
  const [dropTarget, setDropTarget] = useState(null); // field name when dragging over a drop zone
  const [useAi, setUseAi] = useState(false);
  const [analysisResult, setAnalysisResult] = useState(null);
  const [localPropertyReference, setLocalPropertyReference] = useState(() => `LP-${Math.floor(100000 + Math.random() * 900000)}`);
  const [formData, setFormData] = useState(initialFormState);

  const isResidential = useMemo(
    () => formData.propertyCategory === 'apartment' || formData.propertyCategory === 'house',
    [formData.propertyCategory]
  );

  const listingTitle = useMemo(
    () => generateListingTitle(formData.propertyCategory, formData.saleType),
    [formData.propertyCategory, formData.saleType]
  );

  const handleFileSelect = async (e) => {
    const input = e.target;
    const files = Array.from(input.files || []);
    if (!files.length) {
      return;
    }

    const processedFiles = [];

    const VIDEO_MAX_SIZE = 100 * 1024 * 1024; // 100MB for videos
    for (const file of files) {
      const isImage = file.type.startsWith('image/');
      const isVideo = file.type.startsWith('video/');
      if (!isImage && !isVideo) {
        toast.error(`${file.name}: unsupported file type (use images or videos)`);
        continue;
      }

      if (isVideo) {
        if (file.size > VIDEO_MAX_SIZE) {
          toast.error(`${file.name}: video larger than 100MB. Please use a smaller file.`);
          continue;
        }
        processedFiles.push(file);
        continue;
      }

      if (file.size > HARD_MAX_FILE_SIZE) {
        toast.error(`${file.name}: larger than ${Math.round(HARD_MAX_FILE_SIZE / (1024 * 1024))}MB. Please resize before uploading.`);
        continue;
      }

      try {
        // eslint-disable-next-line no-await-in-loop
        const compressed = await compressImageFile(file);

        if (compressed.size > TARGET_MAX_FILE_SIZE) {
          toast.error(`${file.name}: still too large after compression. Please resize manually.`);
          continue;
        }

        processedFiles.push(compressed);
      } catch (err) {
        console.error(`Failed to process ${file.name}`, err);
        toast.error(`${file.name}: could not be processed`);
      }
    }

    setSelectedFiles((prev) => {
      const combined = [...prev, ...processedFiles];
      if (combined.length > MAX_MEDIA_COUNT) {
        toast(`Only the first ${MAX_MEDIA_COUNT} files are kept.`);
      }
      return combined.slice(0, MAX_MEDIA_COUNT);
    });
    setAnalysisResult(null);

    if (processedFiles.length) {
      const skipped = files.length - processedFiles.length;
      toast.success(`Uploading ${processedFiles.length} file${processedFiles.length === 1 ? '' : 's'}…${skipped > 0 ? ` (${skipped} skipped)` : ''}`);
      uploadMediaFiles(processedFiles);
    } else {
      toast.error('No files were added. Please try again with images/videos.');
    }

    input.value = '';
  };

  const uploadMediaFiles = (filesToUpload) => {
    if (!filesToUpload.length) return;
    setUploadMediaStatus('uploading');
    setUploadMediaProgress(0);

    const fd = new FormData();
    filesToUpload.forEach((file, i) => fd.append(`media[${i}]`, file));

    const xhr = new XMLHttpRequest();
    const url = apiUrl('/admin/properties/upload-media');
    xhr.open('POST', url);
    xhr.setRequestHeader('Authorization', `Bearer ${localStorage.getItem('admin_token')}`);

    xhr.upload.addEventListener('progress', (e) => {
      if (e.lengthComputable) {
        setUploadMediaProgress(Math.round((e.loaded / e.total) * 100));
      } else {
        setUploadMediaProgress((prev) => Math.min(95, (prev || 0) + 10));
      }
    });

    xhr.addEventListener('load', () => {
      setUploadMediaProgress(100);
      try {
        const result = JSON.parse(xhr.responseText || '{}');
        if (xhr.status >= 200 && xhr.status < 300 && result.success && result.data?.items?.length) {
          setUploadedMedia((prev) => {
            const next = [...prev, ...result.data.items];
            return next.slice(0, MAX_MEDIA_COUNT);
          });
          setUploadMediaStatus('done');
          toast.success(`${result.data.items.length} file(s) uploaded.`);
        } else {
          setUploadMediaStatus('error');
          toast.error(result.message || 'Upload failed');
        }
      } catch {
        setUploadMediaStatus('error');
        toast.error('Upload failed');
      }
    });
    xhr.addEventListener('error', () => {
      setUploadMediaStatus('error');
      toast.error('Upload failed');
    });
    xhr.send(fd);
  };

  const applyExtractedDataToForm = (extracted) => {
    setFormData((prev) => {
      const next = { ...prev };

      if (extracted.property_type) {
        next.propertyCategory = normalizePropertyCategory(extracted.property_type);
      }

      if (extracted.sale_type) {
        next.saleType = normalizeSaleType(extracted.sale_type);
      }

      if (extracted.contact_name) {
        next.contactName = extracted.contact_name;
      }

      const primaryPhone = extracted.phone_number || (Array.isArray(extracted.phone_numbers) ? extracted.phone_numbers.find(Boolean) : null);
      if (primaryPhone) {
        next.phone = primaryPhone;
      }

      if (extracted.whatsapp_number) {
        next.whatsapp = String(extracted.whatsapp_number).trim();
      }

      if (extracted.email) {
        next.email = extracted.email;
      }

      if (extracted.address) {
        next.address = extracted.address;
      }

      if (extracted.has_title) {
        const normalized = extracted.has_title.toString().toLowerCase();
        if (['yes', 'no', 'not_sure'].includes(normalized)) {
          next.hasTitle = normalized;
        }
      }

      if (extracted.lot_size) {
        const numericLot = extractNumericValue(extracted.lot_size);
        next.lotSizeValue = numericLot !== null ? numericLot : extracted.lot_size;
      }

      if (extracted.size_unit) {
        next.lotSizeUnit = normalizeLotUnitForForm(extracted.size_unit);
      }

      if (extracted.living_area_value) {
        next.livingAreaValue = extracted.living_area_value;
      }

      if (extracted.living_area_unit) {
        next.livingAreaUnit = extracted.living_area_unit;
      }

      if (extracted.bedrooms !== undefined && extracted.bedrooms !== null) {
        next.bedrooms = String(extracted.bedrooms);
      }

      if (extracted.bathrooms !== undefined && extracted.bathrooms !== null) {
        next.bathrooms = String(extracted.bathrooms);
      }

      if (extracted.year_built) {
        next.yearBuilt = extracted.year_built;
      }

      const numericPrice = extractNumericValue(extracted.price);
      if (numericPrice !== null) {
        next.price = numericPrice;
      }

      if (extracted.currency) {
        next.currency = normalizeCurrency(extracted.currency);
      }

      if (extracted.description) {
        next.description = extracted.description;
      } else if (extracted.notes) {
        next.description = extracted.notes;
      }

      return next;
    });
  };

  const handleAiSnippetDragStart = (e, key, value) => {
    const text = Array.isArray(value) ? value.join(', ') : String(value);
    e.dataTransfer.setData('text/plain', text);
    e.dataTransfer.effectAllowed = 'copy';
    e.dataTransfer.setData('application/x-salesign-ai-key', key);
  };

  const handleFieldDragOver = (e, fieldName) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
    setDropTarget(fieldName);
  };

  const handleFieldDragLeave = () => {
    setDropTarget(null);
  };

  const handleFieldDrop = (e, fieldName) => {
    e.preventDefault();
    setDropTarget(null);
    const text = e.dataTransfer.getData('text/plain');
    if (!text) return;
    setFormData((prev) => ({ ...prev, [fieldName]: text }));
  };

  const handleUploadAndAnalyze = async () => {
    const firstImage = selectedFiles.find((f) => f.type.startsWith('image/'));
    if (!firstImage) {
      toast.error('Please add at least one image to analyze.');
      return;
    }

    try {
      const token = localStorage.getItem('admin_token');

      const formDataToSend = new FormData();
      formDataToSend.append('image', firstImage);
      formDataToSend.append('title', listingTitle);
      formDataToSend.append('description', formData.description);

      const response = await fetch(apiUrl('/admin/properties/upload-analyze'), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formDataToSend
      });

      if (response.status === 413) {
        toast.error('The selected image is still too large after compression. Please upload a smaller photo.');
        return;
      }

      let result;
      try {
        result = await response.json();
      } catch (err) {
        throw new Error('Server returned an unexpected response. Please try again.');
      }

      if (!response.ok) {
        if (response.status === 401) {
          localStorage.removeItem('admin_token');
          localStorage.removeItem('admin_user');
          window.location.href = '/admin/login';
          return;
        }
        throw new Error(result.message || `Upload failed: ${response.status}`);
      }

      if (result.success) {
        setAnalysisResult(result.data);
        const extracted = result.data?.extracted_data || {};
        applyExtractedDataToForm(extracted);

        const locationData = result.data?.location_data;
        const exifLocation = result.data?.exif_location;

        if (locationData?.coordinates) {
          const coords = locationData.coordinates;
          if (Array.isArray(coords) && coords.length >= 2) {
            setFormData((prev) => ({ ...prev, latitude: coords[0], longitude: coords[1] }));
          } else if (coords.lat && coords.lng) {
            setFormData((prev) => ({ ...prev, latitude: coords.lat, longitude: coords.lng }));
          }
        } else if (exifLocation?.latitude && exifLocation?.longitude) {
          setFormData((prev) => ({ ...prev, latitude: exifLocation.latitude, longitude: exifLocation.longitude }));
        }

        toast.success('Image analyzed successfully! Form auto-filled with extracted data.');
      } else {
        toast.error(result.message || 'Failed to analyze image');
      }
    } catch (error) {
      console.error('Upload and analysis error:', error);
      toast.error(`Failed to analyze image: ${error.message}`);
    }
  };

  const [extractingExif, setExtractingExif] = useState(false);
  const [exifEndpoint404, setExifEndpoint404] = useState(false);

  const handleExtractExifFromFirstImage = async () => {
    if (exifEndpoint404) return;
    const firstImage = selectedFiles.find((f) => f.type.startsWith('image/'));
    if (!firstImage) {
      toast.error('Please add at least one image for EXIF.');
      return;
    }
    setExtractingExif(true);
    try {
      const tryPath = (path) => {
        const fd = new FormData();
        fd.append('image', firstImage);
        return fetch(apiUrl(path), {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            Authorization: `Bearer ${localStorage.getItem('admin_token')}`,
          },
          body: fd,
        });
      };
      let response = await tryPath('/admin/properties/extract-exif');
      if (response.status === 404) {
        response = await tryPath('/admin/properties/extract_exif');
      }
      const result = await response.json().catch(() => ({}));
      // 404 with "No GPS data found" means the route works; only treat as "service unavailable" if route is missing
      const isNoGpsInImage = response.status === 404 && result?.message?.toLowerCase().includes('no gps data');
      if (response.status === 404 && !isNoGpsInImage) {
        setExifEndpoint404(true);
        toast.error('EXIF service not available. Use the latitude/longitude fields or run "php artisan route:clear" on the server.');
        return;
      }
      if (response.ok && result.success && result.data?.latitude != null && result.data?.longitude != null) {
        setFormData((prev) => ({
          ...prev,
          latitude: String(result.data.latitude),
          longitude: String(result.data.longitude),
        }));
        toast.success('Location set from EXIF data of the first photo.');
      } else {
        toast.error(result.message || 'No GPS data in the first photo.');
      }
    } catch (error) {
      console.error('EXIF extract error:', error);
      toast.error('Could not read EXIF from image. You can enter latitude/longitude manually.');
    } finally {
      setExtractingExif(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (useAi && analysisResult) {
      // OK: use AI path
    } else if (uploadedMedia.length === 0) {
      toast.error('Please select images/videos and wait for upload to finish.');
      return;
    }

    if (!formData.latitude || !formData.longitude) {
      toast.error('Please set the property location by placing the pin on the map.');
      return;
    }

    if (!formData.phone.trim()) {
      toast.error('Phone number is required.');
      return;
    }

    if (!formData.description.trim()) {
      toast.error('Please provide a short description.');
      return;
    }

    const payload = {
      client_reference: localPropertyReference,
      property_category: formData.propertyCategory,
      sale_type_detail: formData.saleType,
      property_type: formData.saleType === 'for_rent' ? 'rent' : 'sale',
      is_owner: formData.isOwner,
      contact_name: formData.contactName,
      phone: formData.phone,
      whatsapp: formData.whatsapp || '',
      email: formData.email || '',
      has_title: formData.hasTitle,
      address: formData.address || '',
      city: formData.city || '',
      region: formData.region || '',
      country: formData.country || '',
      lot_size_value: formData.lotSizeValue || '',
      lot_size_unit: mapLotUnitForBackend(formData.lotSizeUnit),
      living_area_value: isResidential ? (formData.livingAreaValue || '') : '',
      living_area_unit: isResidential ? formData.livingAreaUnit : '',
      bedrooms: isResidential ? formData.bedrooms : '0',
      bathrooms: isResidential ? formData.bathrooms : '0',
      year_built: isResidential ? formData.yearBuilt : '',
      price: formData.price || '',
      currency: formData.currency || 'USD',
      description: formData.description,
      latitude: formData.latitude,
      longitude: formData.longitude,
      auto_approve: formData.autoApprove ? '1' : '0',
      title: listingTitle,
      allow_agent_contact: formData.saleType === 'for_sale_by_owner' && formData.isOwner === 'yes' && formData.allowAgentContact === true ? '1' : '0',
    };
    if (formData.propertyCategory === 'land_lot') {
      payload.road_access = formData.roadAccess || '';
      payload.road_surface = formData.roadSurface || '';
      payload.in_community = formData.inCommunity || '';
      if (formData.waterConnection) payload.water_connection = formData.waterConnection;
      if (formData.electricity) payload.electricity = formData.electricity;
    }

    setUploading(true);
      setUploadProgress(useAi && analysisResult ? null : 0);

    try {
      let response;
      if (useAi && analysisResult) {
        response = await fetch(apiUrl('/admin/properties/create-from-upload'), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('admin_token')}`
          },
          body: JSON.stringify({
            ...payload,
            image_path: analysisResult.image_path,
          })
        });
      } else {
        // Use pre-uploaded media (image_paths)
        response = await fetch(apiUrl('/admin/properties/store-with-images'), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('admin_token')}`
          },
          body: JSON.stringify({
            ...payload,
            image_paths: uploadedMedia.map((m) => m.path),
          })
        });
      }

      if (response.status === 413) {
        toast.error('Upload rejected: the combined files are still too large. Please remove or shrink some images.');
        return;
      }

      let result;
      try {
        result = await response.json();
      } catch (err) {
        throw new Error('Server returned an unexpected response. Please try again.');
      }

      if (result.success) {
        toast.success(result.message);
        setSelectedFiles([]);
        setUploadedMedia([]);
        setUploadMediaStatus('idle');
        setAnalysisResult(null);
        setFormData(initialFormState);
        setLocalPropertyReference(`LP-${Math.floor(100000 + Math.random() * 900000)}`);
        if (onPropertyCreated) {
          onPropertyCreated();
        }
      } else {
        const msg = result.message || 'Request failed';
        const errList = result.errors
          ? Object.entries(result.errors)
            .map(([field, msgs]) => `${field}: ${Array.isArray(msgs) ? msgs.join(', ') : msgs}`)
            .join('; ')
          : '';
        toast.error(errList ? `${msg} — ${errList}` : msg, { duration: 6000 });
      }
    } catch (error) {
      console.error('Property creation error:', error);
      toast.error('Failed to create property');
    } finally {
      setUploading(false);
      setUploadProgress(null);
    }
  };

  return (
    <div className="bg-white shadow-xl shadow-gray-200/50 rounded-2xl p-6 md:p-8 transition-shadow duration-200">
      <div className="mb-8">
        <h2 className="text-2xl font-bold text-gray-900 tracking-tight">Upload Property</h2>
        <p className="mt-1 text-sm text-gray-500">Add a new listing with photos and details.</p>
      </div>
      <div className="mb-6 p-4 rounded-xl bg-gray-50/80 border border-gray-100 transition-colors duration-200">
        <label htmlFor="use_ai_toggle" className="flex items-center gap-3 cursor-pointer">
          <input
            id="use_ai_toggle"
            type="checkbox"
            checked={useAi}
            onChange={(e) => setUseAi(e.target.checked)}
            className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:ring-offset-0"
          />
          <span className="text-sm font-medium text-gray-700">Enable AI Inspector (optional)</span>
        </label>
      </div>

      {/* File Upload Section */}
      <div className="mb-8 p-6 border-2 border-dashed border-gray-200 rounded-2xl bg-gray-50/30 hover:border-blue-200 hover:bg-blue-50/20 transition-all duration-300">
        <div className="text-center">
          <input
            type="file"
            accept="image/*,video/*"
            multiple
            onChange={handleFileSelect}
            className="hidden"
            id="property-image-upload"
          />
          <label
            htmlFor="property-image-upload"
            className="cursor-pointer inline-flex items-center px-5 py-2.5 border border-gray-200 rounded-xl text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 hover:border-gray-300 shadow-sm hover:shadow transition-all duration-200"
          >
            📷 Select images / videos (up to 50)
          </label>

          {uploadMediaStatus === 'uploading' && (
            <div className="mt-4 w-full max-w-md mx-auto">
              <p className="text-sm text-gray-700 mb-2">Uploading… {uploadMediaProgress}%</p>
              <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                <div
                  className="h-full bg-blue-500 rounded-full transition-all duration-300"
                  style={{ width: `${uploadMediaProgress}%` }}
                />
              </div>
            </div>
          )}

          {uploadedMedia.length > 0 && (
            <div className="mt-4">
              <p className="text-sm font-medium text-gray-700 mb-2">Uploaded ({uploadedMedia.length})</p>
              <div className="flex flex-wrap gap-2">
                {uploadedMedia.map((item, idx) => (
                  <div key={item.path + idx} className="relative w-20 h-20 rounded-xl overflow-hidden border border-gray-200 bg-gray-100 flex-shrink-0">
                    {item.type === 'image' ? (
                      <img src={item.url} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-gray-500">
                        <span className="text-2xl">🎬</span>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {selectedFiles.length > 0 && (
            <div className="mt-4 flex flex-wrap items-center justify-center gap-2">
              <p className="text-sm text-gray-600 w-full">{selectedFiles.length} file(s) selected</p>
              {useAi && (() => {
                const firstImage = selectedFiles.find((f) => f.type.startsWith('image/'));
                return firstImage ? (
                  <button
                    type="button"
                    onClick={handleUploadAndAnalyze}
                    className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-xl text-sm font-medium transition-all duration-200 hover:shadow-md active:scale-[0.98]"
                  >
                    🤖 Analyze first image with AI
                  </button>
                ) : null;
              })()}
              {(() => {
                const firstImage = selectedFiles.find((f) => f.type.startsWith('image/'));
                const exifDisabled = extractingExif || !firstImage || exifEndpoint404;
                return (
                  <button
                    key="exif"
                    type="button"
                    onClick={handleExtractExifFromFirstImage}
                    disabled={exifDisabled}
                    title={exifEndpoint404 ? 'EXIF endpoint not available on server (404). Use latitude/longitude fields.' : undefined}
                    className="bg-amber-600 hover:bg-amber-700 disabled:opacity-50 text-white px-4 py-2 rounded-xl text-sm font-medium transition-all duration-200 hover:shadow-md active:scale-[0.98]"
                  >
                    {extractingExif ? '…' : exifEndpoint404 ? '📍 EXIF unavailable' : '📍 Use location from first photo (EXIF)'}
                  </button>
                );
              })()}
            </div>
          )}
        </div>
      </div>

      {useAi && analysisResult && (
        <div className="mb-8 p-5 bg-blue-50/80 border border-blue-100 rounded-2xl shadow-sm transition-all duration-200">
          <h3 className="text-lg font-medium text-blue-800 mb-3">🤖 AI Analysis Summary</h3>
          <p className="text-xs text-blue-600 mb-3">Drag snippets into the form fields below.</p>

          {analysisResult.image_url && (
            <div className="mb-4">
              <img
                src={analysisResult.image_url}
                alt="Property"
                className="w-full max-w-md h-48 object-cover rounded-xl shadow-sm"
              />
            </div>
          )}

          <div className="grid gap-2 text-sm md:grid-cols-2">
            {analysisResult.extracted_data && Object.entries(analysisResult.extracted_data)
              .filter(([, value]) => value !== null && value !== undefined && value !== '')
              .map(([key, value]) => {
                const displayValue = Array.isArray(value) ? value.join(', ') : String(value);
                return (
                  <div key={key} className="flex items-center gap-2">
                    <span className="font-semibold text-blue-700 capitalize shrink-0">{key.replace(/_/g, ' ')}:</span>
                    <span
                      draggable
                      onDragStart={(e) => handleAiSnippetDragStart(e, key, value)}
                      className="ml-2 px-2 py-1 rounded-lg bg-blue-100/80 text-blue-800 cursor-grab active:cursor-grabbing border border-blue-200/60 hover:border-blue-300 select-none"
                      title="Drag into a form field"
                    >
                      {displayValue}
                    </span>
                  </div>
                );
              })}
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-8">
        <section className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Unique Property ID</label>
            <input
              type="text"
              value={localPropertyReference}
              readOnly
              className="w-full px-3 py-2 border border-gray-200 rounded-xl bg-gray-50 text-gray-600 focus:ring-2 focus:ring-blue-500/20 focus:border-blue-300 transition-colors"
            />
            <p className="mt-1 text-xs text-gray-500">Automatically generated for tracking.</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Listing Title (preview)</label>
            <input
              type="text"
              value={listingTitle}
              readOnly
              className="w-full px-3 py-2 border border-gray-200 rounded-xl bg-gray-50 text-gray-600 focus:ring-2 focus:ring-blue-500/20 focus:border-blue-300 transition-colors"
            />
            <p className="mt-1 text-xs text-gray-500">Generated from property type and sale type.</p>
          </div>
        </section>

        <section className="grid gap-4 md:grid-cols-2">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Property Type *</label>
            <select
              value={formData.propertyCategory}
              onChange={(e) => {
                const value = e.target.value;
                setFormData((prev) => ({
                  ...prev,
                  propertyCategory: value,
                  ...(value === 'land_lot'
                    ? { livingAreaValue: '', bedrooms: '0', bathrooms: '0', yearBuilt: '' }
                    : {}),
                }));
              }}
              className="w-full px-3 py-2 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500/30 focus:border-blue-400 transition-shadow duration-150"
            >
              <option value="land_lot">Land / Lot</option>
              <option value="apartment">Apartment</option>
              <option value="house">House</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Sale Type *</label>
            <select
              value={formData.saleType}
              onChange={(e) => {
                const value = e.target.value;
                setFormData((prev) => ({
                  ...prev,
                  saleType: value,
                  allowAgentContact: value === 'for_sale_by_owner' && prev.isOwner === 'yes' ? prev.allowAgentContact : null,
                }));
              }}
              className="w-full px-3 py-2 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500/30 focus:border-blue-400 transition-shadow duration-150"
            >
              <option value="for_sale">For sale</option>
              <option value="for_sale_by_owner">For sale by owner</option>
              <option value="for_rent">For rent</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('reportPage.form.isOwner')} *</label>
            <select
              value={formData.isOwner}
              onChange={(e) => {
                const value = e.target.value;
                setFormData((prev) => ({
                  ...prev,
                  isOwner: value,
                  allowAgentContact: prev.saleType === 'for_sale_by_owner' && value === 'yes' ? prev.allowAgentContact : null,
                }));
              }}
              className="w-full px-3 py-2 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500/30 focus:border-blue-400 transition-shadow duration-150"
            >
              <option value="yes">{t('common.yes')}</option>
              <option value="no">{t('common.no')}</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Property Title *</label>
            <select
              value={formData.hasTitle}
              onChange={(e) => setFormData((prev) => ({ ...prev, hasTitle: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500/30 focus:border-blue-400 transition-shadow duration-150"
            >
              <option value="yes">Yes</option>
              <option value="no">No</option>
              <option value="not_sure">Not sure</option>
            </select>
          </div>
          <div
            onDragOver={(e) => handleFieldDragOver(e, 'contactName')}
            onDragLeave={handleFieldDragLeave}
            onDrop={(e) => handleFieldDrop(e, 'contactName')}
            className={`rounded-xl transition-all duration-150 ${dropTarget === 'contactName' ? 'ring-2 ring-blue-400 ring-offset-1 bg-blue-50/50' : ''}`}
          >
            <label className="block text-sm font-medium text-gray-700 mb-1">Contact Name</label>
            <input
              type="text"
              value={formData.contactName}
              onChange={(e) => setFormData((prev) => ({ ...prev, contactName: e.target.value }))}
              placeholder="e.g., Maria Garcia"
              className="w-full px-3 py-2 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500/30 focus:border-blue-400 transition-shadow duration-150"
            />
          </div>
          <div
            onDragOver={(e) => handleFieldDragOver(e, 'phone')}
            onDragLeave={handleFieldDragLeave}
            onDrop={(e) => handleFieldDrop(e, 'phone')}
            className={`rounded-xl transition-all duration-150 ${dropTarget === 'phone' ? 'ring-2 ring-blue-400 ring-offset-1 bg-blue-50/50' : ''}`}
          >
            <label className="block text-sm font-medium text-gray-700 mb-1">Phone number *</label>
            <input
              type="text"
              value={formData.phone}
              onChange={(e) => setFormData((prev) => ({ ...prev, phone: e.target.value }))}
              placeholder="+507 1233 43354"
              className="w-full px-3 py-2 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500/30 focus:border-blue-400 transition-shadow duration-150"
              required
            />
          </div>
          <div
            onDragOver={(e) => handleFieldDragOver(e, 'whatsapp')}
            onDragLeave={handleFieldDragLeave}
            onDrop={(e) => handleFieldDrop(e, 'whatsapp')}
            className={`rounded-xl transition-all duration-150 ${dropTarget === 'whatsapp' ? 'ring-2 ring-blue-400 ring-offset-1 bg-blue-50/50' : ''}`}
          >
            <label className="block text-sm font-medium text-gray-700 mb-1">WhatsApp</label>
            <input
              type="tel"
              value={formData.whatsapp}
              onChange={(e) => setFormData((prev) => ({ ...prev, whatsapp: e.target.value }))}
              placeholder="+507 6000 0000"
              className="w-full px-3 py-2 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500/30 focus:border-blue-400 transition-shadow duration-150"
            />
          </div>
          <div
            onDragOver={(e) => handleFieldDragOver(e, 'email')}
            onDragLeave={handleFieldDragLeave}
            onDrop={(e) => handleFieldDrop(e, 'email')}
            className={`rounded-xl transition-all duration-150 ${dropTarget === 'email' ? 'ring-2 ring-blue-400 ring-offset-1 bg-blue-50/50' : ''}`}
          >
            <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
            <input
              type="email"
              value={formData.email}
              onChange={(e) => setFormData((prev) => ({ ...prev, email: e.target.value }))}
              placeholder="contact@example.com"
              className="w-full px-3 py-2 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500/30 focus:border-blue-400 transition-shadow duration-150"
            />
          </div>
        </section>

        <section className="grid gap-4 md:grid-cols-2">
          <div
            onDragOver={(e) => handleFieldDragOver(e, 'address')}
            onDragLeave={handleFieldDragLeave}
            onDrop={(e) => handleFieldDrop(e, 'address')}
            className={`md:col-span-2 rounded-xl transition-all duration-150 ${dropTarget === 'address' ? 'ring-2 ring-blue-400 ring-offset-1 bg-blue-50/50' : ''}`}
          >
            <label className="block text-sm font-medium text-gray-700 mb-1">Address</label>
            <input
              type="text"
              value={formData.address}
              onChange={(e) => setFormData((prev) => ({ ...prev, address: e.target.value }))}
              placeholder="Street, number"
              className="w-full px-3 py-2 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500/30 focus:border-blue-400 transition-shadow duration-150"
            />
          </div>
          <div
            onDragOver={(e) => handleFieldDragOver(e, 'city')}
            onDragLeave={handleFieldDragLeave}
            onDrop={(e) => handleFieldDrop(e, 'city')}
            className={`rounded-xl transition-all duration-150 ${dropTarget === 'city' ? 'ring-2 ring-blue-400 ring-offset-1 bg-blue-50/50' : ''}`}
          >
            <label className="block text-sm font-medium text-gray-700 mb-1">City</label>
            <input
              type="text"
              value={formData.city}
              onChange={(e) => setFormData((prev) => ({ ...prev, city: e.target.value }))}
              placeholder="City / town"
              className="w-full px-3 py-2 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500/30 focus:border-blue-400 transition-shadow duration-150"
            />
          </div>
          <div
            onDragOver={(e) => handleFieldDragOver(e, 'region')}
            onDragLeave={handleFieldDragLeave}
            onDrop={(e) => handleFieldDrop(e, 'region')}
            className={`rounded-xl transition-all duration-150 ${dropTarget === 'region' ? 'ring-2 ring-blue-400 ring-offset-1 bg-blue-50/50' : ''}`}
          >
            <label className="block text-sm font-medium text-gray-700 mb-1">Region</label>
            <input
              type="text"
              value={formData.region}
              onChange={(e) => setFormData((prev) => ({ ...prev, region: e.target.value }))}
              placeholder="Region / state / province"
              className="w-full px-3 py-2 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500/30 focus:border-blue-400 transition-shadow duration-150"
            />
          </div>
          <div
            onDragOver={(e) => handleFieldDragOver(e, 'country')}
            onDragLeave={handleFieldDragLeave}
            onDrop={(e) => handleFieldDrop(e, 'country')}
            className={`rounded-xl transition-all duration-150 ${dropTarget === 'country' ? 'ring-2 ring-blue-400 ring-offset-1 bg-blue-50/50' : ''}`}
          >
            <label className="block text-sm font-medium text-gray-700 mb-1">Country</label>
            <input
              type="text"
              value={formData.country}
              onChange={(e) => setFormData((prev) => ({ ...prev, country: e.target.value }))}
              placeholder="Country"
              className="w-full px-3 py-2 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500/30 focus:border-blue-400 transition-shadow duration-150"
            />
          </div>
        </section>

        {formData.propertyCategory === 'land_lot' && (
          <section className="grid gap-4 md:grid-cols-2">
            <h3 className="text-sm font-semibold text-gray-800 md:col-span-2">Land / lot details</h3>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Road access</label>
              <select
                value={formData.roadAccess}
                onChange={(e) => setFormData((prev) => ({ ...prev, roadAccess: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500/30 focus:border-blue-400"
              >
                <option value="">Select</option>
                <option value="yes">Yes</option>
                <option value="no">No</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Road surface</label>
              <select
                value={formData.roadSurface}
                onChange={(e) => setFormData((prev) => ({ ...prev, roadSurface: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500/30 focus:border-blue-400"
              >
                <option value="">Select</option>
                <option value="asphalt">Asphalt</option>
                <option value="gravel">Gravel</option>
                <option value="dirt">Dirt</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">In community</label>
              <select
                value={formData.inCommunity}
                onChange={(e) => setFormData((prev) => ({ ...prev, inCommunity: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500/30 focus:border-blue-400"
              >
                <option value="">Select</option>
                <option value="yes">Yes</option>
                <option value="no">No</option>
                <option value="not_sure">Not sure</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Water connection</label>
              <select
                value={formData.waterConnection}
                onChange={(e) => setFormData((prev) => ({ ...prev, waterConnection: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500/30 focus:border-blue-400"
              >
                <option value="">Select</option>
                <option value="yes">Yes</option>
                <option value="no">No</option>
                <option value="not_sure">Not sure</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Electricity</label>
              <select
                value={formData.electricity}
                onChange={(e) => setFormData((prev) => ({ ...prev, electricity: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500/30 focus:border-blue-400"
              >
                <option value="">Select</option>
                <option value="yes">Yes</option>
                <option value="no">No</option>
                <option value="not_sure">Not sure</option>
              </select>
            </div>
          </section>
        )}

        {formData.saleType === 'for_sale_by_owner' && formData.isOwner === 'yes' && (
          <section>
            <label className="block text-sm font-medium text-gray-700 mb-1">Allow agents to contact (Maklervermarktung)</label>
            <select
              value={formData.allowAgentContact === true ? 'yes' : formData.allowAgentContact === false ? 'no' : ''}
              onChange={(e) => {
                const v = e.target.value;
                setFormData((prev) => ({ ...prev, allowAgentContact: v === 'yes' ? true : v === 'no' ? false : null }));
              }}
              className="w-full px-3 py-2 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500/30 focus:border-blue-400 max-w-xs"
            >
              <option value="">Select</option>
              <option value="yes">Yes</option>
              <option value="no">No</option>
            </select>
          </section>
        )}

        <section className="grid gap-4 md:grid-cols-2">
          <div
            onDragOver={(e) => handleFieldDragOver(e, 'lotSizeValue')}
            onDragLeave={handleFieldDragLeave}
            onDrop={(e) => handleFieldDrop(e, 'lotSizeValue')}
            className={`rounded-xl transition-all duration-150 ${dropTarget === 'lotSizeValue' ? 'ring-2 ring-blue-400 ring-offset-1 bg-blue-50/50' : ''}`}
          >
            <label className="block text-sm font-medium text-gray-700 mb-1">Size of lot</label>
            <div className="flex gap-2">
              <input
                type="number"
                min="0"
                step="0.01"
                value={formData.lotSizeValue}
                onChange={(e) => setFormData((prev) => ({ ...prev, lotSizeValue: e.target.value }))}
                placeholder="e.g., 500"
                className="flex-1 px-3 py-2 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500/30 focus:border-blue-400 transition-shadow duration-150"
              />
              <select
                value={formData.lotSizeUnit}
                onChange={(e) => setFormData((prev) => ({ ...prev, lotSizeUnit: e.target.value }))}
                className="px-3 py-2 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500/30 focus:border-blue-400 transition-shadow duration-150 bg-gray-50"
              >
                <option value="sqm">Sqm</option>
                <option value="acres">Acres</option>
                <option value="hectares">Hectares</option>
              </select>
            </div>
          </div>
          {isResidential && (
            <div
              onDragOver={(e) => handleFieldDragOver(e, 'livingAreaValue')}
              onDragLeave={handleFieldDragLeave}
              onDrop={(e) => handleFieldDrop(e, 'livingAreaValue')}
              className={`rounded-xl transition-all duration-150 ${dropTarget === 'livingAreaValue' ? 'ring-2 ring-blue-400 ring-offset-1 bg-blue-50/50' : ''}`}
            >
              <label className="block text-sm font-medium text-gray-700 mb-1">Size of living area</label>
              <div className="flex gap-2">
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={formData.livingAreaValue}
                  onChange={(e) => setFormData((prev) => ({ ...prev, livingAreaValue: e.target.value }))}
                  placeholder="e.g., 120"
                  className="flex-1 px-3 py-2 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500/30 focus:border-blue-400 transition-shadow duration-150"
                />
                <select
                  value={formData.livingAreaUnit}
                  onChange={(e) => setFormData((prev) => ({ ...prev, livingAreaUnit: e.target.value }))}
                  className="px-3 py-2 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500/30 focus:border-blue-400 transition-shadow duration-150 bg-gray-50"
                >
                  <option value="sqm">Sqm</option>
                  <option value="sqft">Sqft</option>
                </select>
              </div>
            </div>
          )}
          {isResidential && (
            <>
              <div
                onDragOver={(e) => handleFieldDragOver(e, 'bedrooms')}
                onDragLeave={handleFieldDragLeave}
                onDrop={(e) => handleFieldDrop(e, 'bedrooms')}
                className={`rounded-xl transition-all duration-150 ${dropTarget === 'bedrooms' ? 'ring-2 ring-blue-400 ring-offset-1 bg-blue-50/50' : ''}`}
              >
                <label className="block text-sm font-medium text-gray-700 mb-1">Number of bedrooms</label>
                <select
                  value={formData.bedrooms}
                  onChange={(e) => setFormData((prev) => ({ ...prev, bedrooms: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500/30 focus:border-blue-400 transition-shadow duration-150"
                >
                  {BEDROOM_OPTIONS.map((option) => (
                    <option key={`bedroom-${option}`} value={option}>{option}</option>
                  ))}
                </select>
              </div>
              <div
                onDragOver={(e) => handleFieldDragOver(e, 'bathrooms')}
                onDragLeave={handleFieldDragLeave}
                onDrop={(e) => handleFieldDrop(e, 'bathrooms')}
                className={`rounded-xl transition-all duration-150 ${dropTarget === 'bathrooms' ? 'ring-2 ring-blue-400 ring-offset-1 bg-blue-50/50' : ''}`}
              >
                <label className="block text-sm font-medium text-gray-700 mb-1">Number of bathrooms</label>
                <select
                  value={formData.bathrooms}
                  onChange={(e) => setFormData((prev) => ({ ...prev, bathrooms: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500/30 focus:border-blue-400 transition-shadow duration-150"
                >
                  {BEDROOM_OPTIONS.map((option) => (
                    <option key={`bathroom-${option}`} value={option}>{option}</option>
                  ))}
                </select>
              </div>
              <div
                onDragOver={(e) => handleFieldDragOver(e, 'yearBuilt')}
                onDragLeave={handleFieldDragLeave}
                onDrop={(e) => handleFieldDrop(e, 'yearBuilt')}
                className={`rounded-xl transition-all duration-150 ${dropTarget === 'yearBuilt' ? 'ring-2 ring-blue-400 ring-offset-1 bg-blue-50/50' : ''}`}
              >
                <label className="block text-sm font-medium text-gray-700 mb-1">Year built</label>
                <input
                  type="number"
                  min="1800"
                  max={new Date().getFullYear()}
                  value={formData.yearBuilt}
                  onChange={(e) => setFormData((prev) => ({ ...prev, yearBuilt: e.target.value }))}
                  placeholder="e.g., 2015"
                  className="w-full px-3 py-2 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500/30 focus:border-blue-400 transition-shadow duration-150"
                />
              </div>
            </>
          )}
        </section>

        <section className="grid gap-4 md:grid-cols-2">
          <div
            onDragOver={(e) => handleFieldDragOver(e, 'price')}
            onDragLeave={handleFieldDragLeave}
            onDrop={(e) => handleFieldDrop(e, 'price')}
            className={`rounded-xl transition-all duration-150 ${dropTarget === 'price' ? 'ring-2 ring-blue-400 ring-offset-1 bg-blue-50/50' : ''}`}
          >
            <label className="block text-sm font-medium text-gray-700 mb-1">Price</label>
            <div className="flex gap-2">
              <input
                type="number"
                min="0"
                step="0.01"
                value={formData.price}
                onChange={(e) => setFormData((prev) => ({ ...prev, price: e.target.value }))}
                placeholder="e.g., 250000"
                className="flex-1 px-3 py-2 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500/30 focus:border-blue-400 transition-shadow duration-150"
              />
              <select
                value={formData.currency}
                onChange={(e) => setFormData((prev) => ({ ...prev, currency: e.target.value }))}
                className="px-3 py-2 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500/30 focus:border-blue-400 transition-shadow duration-150 bg-gray-50"
              >
                <option value="USD">USD</option>
                <option value="EUR">EUR</option>
                <option value="CNY">CNY</option>
              </select>
            </div>
          </div>
          <div
            className={`md:col-span-2 rounded-xl transition-all duration-150 ${dropTarget === 'description' ? 'ring-2 ring-blue-400 ring-offset-1 bg-blue-50/50' : ''}`}
            onDragOver={(e) => handleFieldDragOver(e, 'description')}
            onDragLeave={handleFieldDragLeave}
            onDrop={(e) => handleFieldDrop(e, 'description')}
          >
            <label className="block text-sm font-medium text-gray-700 mb-1">Description *</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData((prev) => ({ ...prev, description: e.target.value }))}
              rows={4}
              className="w-full px-3 py-2 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500/30 focus:border-blue-400 transition-shadow duration-150"
              placeholder="Describe the property..."
              required
            />
          </div>
        </section>

        <section>
          <label className="block text-sm font-medium text-gray-700 mb-2">Location *</label>
          <p className="text-xs text-gray-500 mb-2">Set pin on map or enter coordinates (e.g. paste: 8.571496, -79.902399).</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Latitude</label>
              <input
                type="text"
                inputMode="decimal"
                value={formData.latitude}
                onChange={(e) => setFormData((prev) => ({ ...prev, latitude: e.target.value.trim() }))}
                placeholder="e.g. 8.571496"
                className="w-full px-3 py-2 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500/30 focus:border-blue-400 text-sm"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Longitude</label>
              <input
                type="text"
                inputMode="decimal"
                value={formData.longitude}
                onChange={(e) => setFormData((prev) => ({ ...prev, longitude: e.target.value.trim() }))}
                placeholder="e.g. -79.902399"
                className="w-full px-3 py-2 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500/30 focus:border-blue-400 text-sm"
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-xs font-medium text-gray-600 mb-1">Paste coordinates (lat, lng)</label>
              <input
                type="text"
                inputMode="decimal"
                placeholder="8.571496, -79.902399"
                className="w-full px-3 py-2 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500/30 focus:border-blue-400 text-sm"
                onPaste={(e) => {
                  const pasted = (e.clipboardData?.getData('text') || '').trim();
                  const match = pasted.match(/^(-?\d+\.?\d*)\s*[,;\s]\s*(-?\d+\.?\d*)$/);
                  if (match) {
                    e.preventDefault();
                    const lat = match[1];
                    const lng = match[2];
                    setFormData((prev) => ({ ...prev, latitude: lat, longitude: lng }));
                    toast.success('Coordinates applied');
                  }
                }}
                onBlur={(e) => {
                  const v = e.target.value.trim();
                  if (!v) return;
                  const match = v.match(/^(-?\d+\.?\d*)\s*[,;\s]\s*(-?\d+\.?\d*)$/);
                  if (match) {
                    setFormData((prev) => ({ ...prev, latitude: match[1], longitude: match[2] }));
                    e.target.value = '';
                    toast.success('Coordinates applied');
                  }
                }}
              />
            </div>
          </div>
          <div className="rounded-2xl border border-gray-200 overflow-hidden shadow-sm">
            <MapComponent
              height="320px"
              center={formData.latitude && formData.longitude ? [parseFloat(formData.latitude), parseFloat(formData.longitude)] : [8.975585, -79.520459]}
              zoom={formData.latitude && formData.longitude ? 15 : 12}
              markers={(formData.latitude && formData.longitude) ? [{
                id: 'new-property',
                lat: parseFloat(formData.latitude),
                lng: parseFloat(formData.longitude),
                title: listingTitle,
                color: '#10B981'
              }] : []}
              onMapClick={(latlng) => {
                setFormData((prev) => ({ ...prev, latitude: latlng.lat, longitude: latlng.lng }));
              }}
              draggableMarkerId={formData.latitude && formData.longitude ? 'new-property' : null}
              onMarkerDrag={(marker) => {
                setFormData((prev) => ({ ...prev, latitude: marker.lat, longitude: marker.lng }));
              }}
              showControls={true}
            />
          </div>
          <div className="mt-2 text-xs text-gray-600">
            {formData.latitude && formData.longitude ? (
              <span>Set to: {Number(formData.latitude).toFixed(6)}, {Number(formData.longitude).toFixed(6)}</span>
            ) : (
              <span>Please click on the map to place the pin.</span>
            )}
          </div>
        </section>

        {uploading && (
          <section className="rounded-xl bg-gray-50 border border-gray-100 p-4">
            <div className="flex items-center justify-between gap-3 mb-2">
              <span className="text-sm font-medium text-gray-700">
                {uploadProgress != null ? 'Uploading images…' : 'Saving property…'}
              </span>
              {uploadProgress != null && (
                <span className="text-sm tabular-nums text-gray-600">{uploadProgress}%</span>
              )}
            </div>
            <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
              {uploadProgress != null ? (
                <div
                  className="h-full bg-green-500 rounded-full transition-all duration-300 ease-out"
                  style={{ width: `${uploadProgress}%` }}
                />
              ) : (
                <div className="h-full w-full bg-green-400 rounded-full animate-pulse" />
              )}
            </div>
          </section>
        )}

        <section className="flex items-center justify-between">
          <label className="flex items-center space-x-2 text-sm text-gray-700">
            <input
              type="checkbox"
              checked={formData.autoApprove}
              onChange={(e) => setFormData((prev) => ({ ...prev, autoApprove: e.target.checked }))}
              className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:ring-offset-0"
            />
            <span>Auto-approve property (skip manual review)</span>
          </label>

          <button
            type="submit"
            disabled={uploading || uploadMediaStatus === 'uploading' || (useAi && !analysisResult) || (!useAi && uploadedMedia.length === 0)}
            className="bg-green-600 hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white px-6 py-3 rounded-xl font-medium flex items-center transition-all duration-200 hover:shadow-md active:scale-[0.98]"
          >
            {uploading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Creating Property...
              </>
            ) : (
              'Create Property'
            )}
          </button>
        </section>
      </form>
    </div>
  );
};

export default AdminPropertyUpload;

